<template>
  <CollapsibleSection :title="title" :badge="items.length || null" :section-key="sectionKey">
    <template #title>
      <div class="section-title-with-actions">
        <span class="section-title-text">
          <slot name="title">
            <span>{{ title }}</span>
          </slot>
        </span>
        <div class="section-header-actions is-hidden-desktop">
          <button
            v-if="items.length > 0"
            type="button"
            class="button is-small is-danger is-outlined has-white-bg section-clear-btn"
            @click.stop="handleClear"
          >
            <span class="icon"><i class="fas fa-trash"></i></span>
            <span>{{ clearLabel }}</span>
          </button>
        </div>
      </div>
    </template>

    <slot name="extra-content-top"></slot>

    <p v-if="items.length === 0" class="has-text-grey is-size-7 mb-3">
      {{ emptyMessage }}
    </p>

    <slot name="extra-content-after-buttons"></slot>

    <div :class="listClass" @focusin="handleContainerFocusIn" @focusout="handleContainerFocusOut">
      <IngredientItem
        v-for="(line, index) in items"
        :key="line._uid"
        :uid="line._uid"
        :data-uid="line._uid"
        :is-dv-line="isDvLine"
        :start-expanded="index === justAddedIndex"
        :expanded="isExpanded(line._uid)"
        :active="isActive(line._uid)"
        :editing="isEditing(line._uid)"
        :drag-enabled="false"
        @removed="handleFocusAfterRemove"
        @toggle="handleToggle(line._uid)"
        @invalid-dv-input="handleInvalidDvInput"
      />
    </div>

    <div class="buttons mb-0 mt-3 section-buttons is-hidden-touch">
      <button
        type="button"
        class="button is-small is-info is-outlined section-add-btn"
        @click="handleAdd"
      >
        <span class="icon"><i class="fas fa-plus"></i></span>
        <span>{{ addLabel }}</span>
      </button>
      <button
        v-if="items.length > 0"
        type="button"
        class="button is-small is-danger is-outlined section-clear-btn"
        @click="handleClear"
      >
        <span class="icon"><i class="fas fa-trash"></i></span>
        <span>{{ clearLabel }}</span>
      </button>
    </div>

    <slot name="extra-content-bottom"></slot>
  </CollapsibleSection>
</template>

<script setup>
  import { inject } from "vue";
  import CollapsibleSection from "./collapsible-section.vue";
  import IngredientItem from "./ingredient-item.vue";
  import { useExpandCollapse } from "./composables/use-expand-collapse";
  import { focusAfterRemove, focusAfterAdd } from "./utils/focus-helpers";

  const props = defineProps({
    title: { type: String, required: true },
    items: { type: Array, required: true },
    sectionKey: { type: String, required: true },
    isDvLine: { type: Boolean, default: false },
    emptyMessage: { type: String, default: "" },
    addLabel: { type: String, default: "Add Ingredient" },
    clearLabel: { type: String, default: "Clear All" },
    clearType: { type: String, default: null },
    justAddedIndex: { type: Number, default: null },
    listClass: { type: String, default: "lines-list" },
    focusSelector: { type: String, required: true },
    // Custom focus handlers for focus-aware sorting
    onSortFocusIn: { type: Function, default: null },
    onSortFocusOut: { type: Function, default: null },
  });

  const requestClear = inject("requestClear", null);

  const emit = defineEmits(["add", "clear", "clear-added-index", "invalid-dv-input"]);

  function handleAdd() {
    emit("add");
    focusAfterAdd(`.${props.listClass} ${props.focusSelector}`, () => emit("clear-added-index"));
  }

  function handleFocusAfterRemove(removedIndex) {
    focusAfterRemove(
      removedIndex,
      `.${props.listClass} ${props.focusSelector}`,
      ".section-add-btn"
    );
  }

  function handleInvalidDvInput(payload) {
    emit("invalid-dv-input", payload);
  }

  // Use composable for expand/collapse logic
  const {
    isExpanded,
    isActive,
    isEditing,
    toggle: handleToggle,
    handleFocusIn: handleExpandFocusIn,
    handleFocusOut: handleExpandFocusOut,
    clear: clearExpandState,
  } = useExpandCollapse({
    items: () => props.items,
    justAddedIndex: () => props.justAddedIndex,
  });

  // Combined focus handlers for both expand/collapse and sorting
  function handleContainerFocusIn(event) {
    handleExpandFocusIn(event);
    props.onSortFocusIn?.(event);
  }

  function handleContainerFocusOut(event) {
    handleExpandFocusOut(event);
    props.onSortFocusOut?.(event);
  }

  function handleClear() {
    if (props.clearType && requestClear) {
      requestClear(props.clearType);
    } else {
      emit("clear");
    }
    clearExpandState();
    focusAfterRemove(-1, `.${props.listClass} ${props.focusSelector}`, ".section-add-btn");
  }
</script>

<style scoped>
  .section-buttons {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    flex-direction: row-reverse;
  }

  .section-buttons .section-add-btn {
    flex: 2;
  }

  .section-buttons .section-clear-btn {
    flex: 1;
  }
</style>
